import * as React from 'react';
import { ChevronLeft, ChevronRight, type LucideProps } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@ui/lib/utils';

import 'react-day-picker/dist/style.css';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function CalendarIconLeft(props: LucideProps) {
  return <ChevronLeft className="size-4" {...props} />;
}

function CalendarIconRight(props: LucideProps) {
  return <ChevronRight className="size-4" {...props} />;
}

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col gap-4 sm:flex-row sm:gap-4',
        month: 'gap-4',
        caption: 'relative flex items-center justify-center pt-1',
        caption_label: 'text-sm font-medium',
        nav: 'flex items-center gap-1',
        nav_button: cn(
          'inline-flex size-7 items-center justify-center rounded-md border border-input bg-transparent p-0 opacity-70 transition-opacity hover:opacity-100',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse',
        head_row: 'flex',
        head_cell: 'w-8 rounded-md text-[0.8rem] font-normal text-muted-foreground',
        row: 'mt-2 flex w-full',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent',
        ),
        day: cn('size-8 rounded-md p-0 font-normal aria-selected:opacity-100'),
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside: 'text-muted-foreground opacity-50',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle: 'rounded-none aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: CalendarIconLeft,
        IconRight: CalendarIconRight,
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
